'use client';
import React, { createContext, useCallback, useContext, useRef } from 'react';
import PropTypes from 'prop-types';

const CarouselPauseContext = createContext(null);

/**
 * Lets nested CTAs pause a parent carousel while a modal is open,
 * without prop drilling through intermediate components.
 * Open modals are ref-counted so closing one of several keeps the carousel paused.
 */
export const CarouselPauseProvider = ({ onModalOpenChange, children }) => {
  const openCountRef = useRef(0);
  const onModalOpenChangeRef = useRef(onModalOpenChange);
  onModalOpenChangeRef.current = onModalOpenChange;

  const reportModalOpenChange = useCallback(isOpen => {
    if (isOpen) {
      openCountRef.current += 1;
    } else {
      openCountRef.current = Math.max(0, openCountRef.current - 1);
    }
    onModalOpenChangeRef.current?.(openCountRef.current > 0);
  }, []);

  return (
    <CarouselPauseContext.Provider value={reportModalOpenChange}>
      {children}
    </CarouselPauseContext.Provider>
  );
};

CarouselPauseProvider.propTypes = {
  onModalOpenChange: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export const useCarouselModalPause = () => useContext(CarouselPauseContext);
